import { toast } from 'component-library';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  HabitatIntactnessAndThreats,
  IndicatorName,
  LayerFactValue,
  LayerMetadata,
  MAP_LAYER_FACTS,
  RasterLayerYear,
} from '@/api/rest/resources/types/fact';
import { IUCN_STATIC_HASH } from '@/config/constants';
import { Logger } from '@/lib/logs/logger';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { WMS_PARAMS } from '@/pages/shared/plots/pages/hooks/useLayerUrl';
import { isNullish } from '@/utils/booleans';
import { MapBoxRequestMethod } from '@/utils/mapServer';

import { LayerProps, MAP_ORIGIN } from '../constants';

export const useMapEndpointPlot = (
  layer: LayerProps | undefined,
  year: string,
  legendFilter?: string | null,
  mapFilter?: string | null,
): string => {
  const value = layer?.value ?? IndicatorName.unknown;
  const plotFacts = usePlotFacts<string | LayerFactValue>([value]);
  const { t } = useTranslation();

  const factValue = getFactFromFactArray<string | LayerFactValue>(plotFacts, value)?.value;

  const layerName = layer?.value ?? '';
  const layerOrigin = layer?.origin || MAP_ORIGIN.cckp;

  const hasUrlFromFact = layerOrigin === MAP_ORIGIN.fact && !isNullish(factValue);
  const isHabitatIntactnessLayer = layerName === MAP_LAYER_FACTS.habitat_intactness_raster_urls;
  const isTimeSeries = layer?.isTimeSeries;

  const filter =
    legendFilter || mapFilter ? `&CQL_FILTER=${[legendFilter, mapFilter].filter(Boolean).join(' AND ')}` : '';

  if (hasUrlFromFact && isHabitatIntactnessLayer) {
    const rasterLayerType = layer?.layerSubType as HabitatIntactnessAndThreats;
    const baseUrl = (factValue as LayerFactValue)?.[`${rasterLayerType}`]?.[year as RasterLayerYear]?.map_url;

    if (isNullish(baseUrl)) {
      raiseMapError(t, layerName, year, factValue);
    }
    return `${baseUrl}${WMS_PARAMS}${filter}`;
  }

  if (hasUrlFromFact) {
    const baseUrl = isTimeSeries ? (factValue as Record<string, string>)?.[`${year}`] : (factValue as string);
    if (isNullish(baseUrl)) {
      raiseMapError(t, layerName, year, factValue);
    }

    return `${baseUrl}${WMS_PARAMS}${filter}`;
  }

  return getMapEndpoint(layerOrigin, layerName, year, filter);
};

export const useMapEndpointOrg = (
  layer: LayerProps | undefined,
  year: string,
  legendFilter?: string | null,
  mapFilter?: string | null,
): {
  url: string;
  method: MapBoxRequestMethod;
} => {
  const layerName = layer?.value ?? IndicatorName.unknown;
  const { [layerName]: fact } = useOrganizationIndicatorReportingFact<string | Record<string, string>>(layerName);
  const { t } = useTranslation();

  const factValue = fact?.value;

  const origin = layer?.origin || MAP_ORIGIN.cckp;

  const hasUrlFromFact = origin === MAP_ORIGIN.fact && !isNullish(factValue) && factValue;
  const isHabitatIntactnessLayer = layerName === MAP_LAYER_FACTS.habitat_intactness_raster_urls;

  const filter =
    legendFilter || mapFilter ? `&CQL_FILTER=${[legendFilter, mapFilter].filter(Boolean).join(' AND ')}` : '';
  const isTimeSeries = layer?.isTimeSeries;

  if (hasUrlFromFact && !isHabitatIntactnessLayer) {
    const baseURL = isTimeSeries ? (factValue as Record<string, string>)?.[`${year}`] : (factValue as string);
    if (isNullish(baseURL)) {
      raiseMapError(t, layerName, year, factValue);
    }
    return { url: `${baseURL}${WMS_PARAMS}${filter}`, method: 'GET' };
  }

  if (hasUrlFromFact && isHabitatIntactnessLayer) {
    const rasterLayerType = layer?.layerSubType as HabitatIntactnessAndThreats;
    const baseUrl = (factValue as LayerFactValue)?.[`${rasterLayerType}`]?.[year as RasterLayerYear]?.map_url;
    if (isNullish(baseUrl)) {
      raiseMapError(t, layerName, year, factValue);
    }
    return { url: `${baseUrl}${WMS_PARAMS}${filter}`, method: 'GET' };
  }

  return { url: getMapEndpoint(origin, layerName, year, filter), method: 'GET' };
};

const getMapserverBaseUrl = (origin: string, layer: string) => {
  if (origin === MAP_LAYER_FACTS.unknown) {
    return '';
  }
  if (origin === MAP_ORIGIN.gbif) {
    return 'https://api.gbif.org/v2/map/occurrence/density/{z}/{x}/{y}@1x.png';
  }
  // SUPER HACK: to make iucn url unguessable. Hash to be removed when keycloak is setup on geoserver
  if (layer === MAP_LAYER_FACTS.iucn_protected_areas) {
    return `https://mapserver.landler.io/geoserver/${origin}/wms?service=WMS&version=1.1.0&request=GetMap&layers=${origin}:${layer}_${IUCN_STATIC_HASH}`;
  }

  return `https://mapserver.landler.io/geoserver/${origin}/wms?service=WMS&version=1.1.0&request=GetMap&layers=${origin}:${layer}`;
};

export const getMapEndpoint = (origin: string, layer: string, year: string, filter = '') => {
  const baseUrl = getMapserverBaseUrl(origin, layer);
  const baseURLParams = `format=image/png&srs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256${filter}`;

  if (origin === MAP_ORIGIN.gbif) {
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `${baseUrl}?style=scaled.circles&srs=EPSG:3857&basisOfRecord=HUMAN_OBSERVATION&basisOfRecord=MACHINE_OBSERVATION&basisOfRecord=OBSERVATION&basisOfRecord=OCCURRENCE&${year ? `&year=${year}` : ''}${filter}`;
  }

  if (origin === MAP_ORIGIN.terrascope) {
    return `${baseUrl}_2021&${baseURLParams}`;
  }

  if (layer === MAP_LAYER_FACTS.combined_ibat_lulc_urls) {
    return `${baseUrl}_${year}&${baseURLParams}`;
  }

  if (
    [MAP_ORIGIN.population_density, MAP_ORIGIN.esa].includes(origin as string as typeof MAP_ORIGIN.population_density)
  ) {
    return `${baseUrl}_${year}&transparent=true&${baseURLParams}`;
  }

  const yearFilter = year ? `&time=${year}` : '';
  return `${baseUrl}${yearFilter}&transparent=true&${baseURLParams}`;
};

const raiseMapError = (
  t: TFunction<'translation', undefined>,
  layerName: string,
  year: string,
  factValue:
    | string
    | Partial<Record<HabitatIntactnessAndThreats, Record<`20${number}`, LayerMetadata>>>
    | null
    | undefined,
) => {
  const text = t('shared.projects.map.errors.mapLayerError');
  toast({
    type: 'error',
    title: text,
  });
  Logger.error(`Map error for layer: ${layerName}, year: ${year}, factValue: ${JSON.stringify(factValue)}`);
  throw new Error(text);
};
